import { useCustomerAddressesStorageData } from "../customerAddressesStorage";
import { useGetShippingZonesQuery } from "../shippingAndPayment";
import { useGetCustomerAddresses } from "../CustomerAddresses";
import { useAuth } from "../../contexts/auth-context";
import { getAddressByIdBasedOnAuthStatus } from "../../utils/getAddressByIdBasedOnAuthStatus";
import { getMainShippingZone } from "../../utils/getMainShippingZone";
import { ShippingZone } from "@core/types";
import { useMemo } from "react";
import { useConfig } from "../config";
import { shouldUseStaticOrderData } from "../../utils/shouldUseStaticOrderData";

type ShippingZonesDataProps = {
  isLoading: boolean;
  activeShippingZone: ShippingZone;
};

const DEFAULT_CITY = "Грозный";

const useActiveOrMainShippingZone = (): ShippingZonesDataProps => {
  const { user, isUserAuthenticated } = useAuth();
  const {
    data: fetchedShippingZones = [],
    isLoading: isShippingZonesLoading,
    isError: isShippingZonesError,
  } = useGetShippingZonesQuery();
  const config = useConfig();
  const getCustomerAddressesEnabled = !!user?.id;
  const { data: customerAddresses, isLoading: isCustomerAddressesLoading } = useGetCustomerAddresses({
    customerId: user?.id,
    options: {
      enabled: getCustomerAddressesEnabled,
    },
  });

  const { unAuthedCustomerAddresses, currentShippingAddressId } = useCustomerAddressesStorageData();

  const shouldUseStaticZones = shouldUseStaticOrderData({
    staticOrderDataEnabled: config.staticOrderDataEnabled,
    staticOrderData: config.staticOrderData,
    isLoading: isShippingZonesLoading,
    isError: isShippingZonesError,
  });

  const shippingZones = shouldUseStaticZones ? config.staticOrderData!.shippingZones : fetchedShippingZones;

  const activeShippingZone = useMemo(() => {
    if (
      !shippingZones.length ||
      isShippingZonesLoading ||
      (isCustomerAddressesLoading && getCustomerAddressesEnabled)
    ) {
      return { id: config.defaultShippingZoneId, name: DEFAULT_CITY } as ShippingZone;
    }

    const customerAddress = getAddressByIdBasedOnAuthStatus(
      isUserAuthenticated ?? false,
      currentShippingAddressId,
      customerAddresses || [],
      unAuthedCustomerAddresses,
    );

    const customerAddressCity = customerAddress?.city || DEFAULT_CITY;

    const matchingShippingZone = shippingZones.find((zone) => customerAddressCity.includes(zone.name));

    return matchingShippingZone || getMainShippingZone(shippingZones);
  }, [
    shippingZones,
    customerAddresses,
    isUserAuthenticated,
    isShippingZonesLoading,
    currentShippingAddressId,
    unAuthedCustomerAddresses,
    isCustomerAddressesLoading,
    getCustomerAddressesEnabled,
  ]);

  return {
    activeShippingZone,
    isLoading: isShippingZonesLoading || (isCustomerAddressesLoading && getCustomerAddressesEnabled),
  };
};

export default useActiveOrMainShippingZone;
